<template>
  <div
    :class="[
      'chat-panel',
      {
        'chat-panel--history': selectedSessionHasMessages,
        'chat-panel--with-background': hasChatBackground
      }
    ]"
    :style="chatPanelStyle"
  >
    <v-row
      no-gutters
      class="chat-panel-grid"
    >
      <v-col
        cols="12"
        sm="12"
        md="12"
        class="chat-panel-header-col"
      >
        <div class="chat-panel-header">
          <v-spacer />

          <v-btn
            icon
            x-small
            class="close-button"
            aria-label="Close assistant"
            @click="closeSidebar"
          >
            <v-icon size="18">
              mdi-close
            </v-icon>
          </v-btn>
        </div>
      </v-col>

      <v-col
        cols="12"
        sm="12"
        md="12"
        class="chat-panel-content-col"
      >
        <div class="chat-panel-content">
          <transition
            name="content-switch"
            mode="out-in"
          >
            <past-activity
              v-if="showPastActivity"
              key="past-activity"
              @session-selected="$emit('session-selected')"
              @delete-session="$emit('delete-session', $event)"
            />

            <div
              v-else-if="showNewChatLanding"
              key="new-chat"
              class="new-chat-content"
            >
              <v-row
                no-gutters
                class="new-chat-grid"
              >
                <v-col
                  cols="12"
                  sm="12"
                  md="12"
                  class="greeting-col"
                >
                  <chat-greeting />
                </v-col>

                <v-col
                  cols="12"
                  sm="12"
                  md="12"
                  class="suggestions-col"
                >
                  <chat-suggestions @select="sendSuggestion" />
                </v-col>
              </v-row>
            </div>

            <chat-history
              v-else
              key="history"
            />
          </transition>
        </div>
      </v-col>

      <v-col
        v-if="!showPastActivity"
        cols="12"
        sm="12"
        md="12"
        class="chat-panel-footer-col"
      >
        <div class="chat-panel-footer">
          <chat-input />

          <div class="disclaimer">
            Newton is AI and can make mistakes.
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import assistantService from '@/ai/services/AssistantService'

import ChatGreeting from './ChatGreeting.vue'
import ChatSuggestions from './ChatSuggestions.vue'
import ChatHistory from './ChatHistory.vue'
import ChatInput from './ChatInput.vue'
import PastActivity from './PastActivity.vue'

export default {
  name: 'ChatPanel',

  components: {
    ChatGreeting,
    ChatSuggestions,
    ChatHistory,
    ChatInput,
    PastActivity
  },

  dataStore: {
    assistantStore: 'AssistantStore'
  },

  props: {
    viewMode: {
      type   : String,
      default: 'chat'
    }
  },

  computed: {
    selectedSession () {
      return this.assistantStore.SelectedSession.Data
    },

    messages () {
      return this.assistantStore.Messages.Data || []
    },

    selectedSessionHasMessages () {
      return (
        this.showPastActivity ||
        Boolean(this.selectedSession) ||
        this.messages.length > 0
      )
    },

    hasChatBackground () {
      return Boolean(assistantService.chatUrl)
    },

    chatPanelStyle () {
      if (!this.hasChatBackground) {
        return null
      }

      return {
        '--chat-panel-background-image': `url("${assistantService.chatUrl}")`
      }
    },

    showNewChatLanding () {
      return (
        !this.selectedSession &&
        this.messages.length === 0
      )
    },

    showPastActivity () {
      return this.viewMode === 'past-activity'
    }
  },

  methods: {
    sendSuggestion (suggestion) {
      const text = suggestion.content

      if (!text) {
        return
      }

      assistantService.sendMessage(text)
    },

    closeSidebar () {
      assistantService.closeSidebar()
    }
  }
}
</script>

<style scoped>
.chat-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;

  background:
    radial-gradient(
      88.93% 135.62% at 50% -35.62%,
      #EBFFEE 0%,
      #E7ECFA 78.96%
    );

  color: #eef2fc;
  overflow: hidden;
  box-sizing: border-box;
}

.chat-panel::before {
  content: none;
}

.chat-panel--with-background::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  min-width: 400px;
  background-image: var(--chat-panel-background-image);
  background-repeat: no-repeat;
  background-position: center bottom;
  background-size: contain;
  pointer-events: none;
  z-index: 1;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.chat-panel--with-background.chat-panel--history::before {
  opacity: 0.16;
}

.chat-panel-grid {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  flex-wrap: nowrap;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  margin: 0;

  align-content: flex-start;
  overflow: hidden;
  z-index: 2;
}

.chat-panel-header-col {
  flex: 0 0 auto;
  width: 100%;
  max-width: 100%;

  min-height: 0;
}

.chat-panel-header {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  height: 32px;
  padding: 6px 14px 0;
  background: transparent !important;
  box-shadow: none !important;
  border: 0;
  box-sizing: border-box;
}

.close-button {
  position: relative !important;
  isolation: isolate !important;
  color: #041f30 !important;
  opacity: 0.9 !important;
}

.close-button::before {
  content: '';
  position: absolute;
  inset: -4.5px;
  border-radius: 8px;
  background: #ffffff !important;
  opacity: 0;
  z-index: -1;
  transition: opacity 0.15s ease;
}

.close-button:hover::before {
  opacity: 1;
}

.chat-panel-content-col {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;

  width: 100%;
  max-width: 100%;

  min-width: 0;
  min-height: 0;

  overflow: hidden;
}

.chat-panel-content {
  position: relative;

  display: flex;
  flex: 1 1 0;
  flex-direction: column;

  width: 100%;
  height: 100%;

  min-width: 0;
  min-height: 0;

  overflow: hidden;
}

.chat-panel-content > * {
  flex: 1 1 auto;

  width: 100%;
  min-width: 0;
  min-height: 0;
}

.new-chat-content {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: 0 24px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}

.new-chat-grid {
  width: 100%;
  margin: 0;
  padding: 0 0 12px;
  align-content: flex-start;
}

.greeting-col {
  padding: 0 0 8px 0;
  margin-bottom: 30px;
}

.suggestions-col {
  min-width: 0;
  padding: 0;
  margin-bottom: 0;
}

.chat-panel-footer-col {
  flex: 0 0 auto;
  width: 100%;
  max-width: 100%;
  min-width: 0;
}

.chat-panel-footer {
  position: relative;
  flex: 0 0 auto;
  width: 100%;
  min-width: 0;
  margin-top: 0;

  padding: 0 16px 12px;

  box-sizing: border-box;
  z-index: 3;
}

.disclaimer {
  margin-top: 8px;
  padding-bottom: 12px;
  color: #041F30;
  font-family: 'Inter';
  font-size: 11px;
  font-weight: 300;
  line-height: 21px;
  text-align: center;
}

.terms-link {
  color: inherit;
  text-decoration: underline;
}

.content-switch-enter-active,
.content-switch-leave-active {
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
}

.content-switch-enter,
.content-switch-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
